/*This is an element representing the add person widget*/
import {PolymerElement, html} from '@polymer/polymer/polymer-element.js';

import '@material/web/dialog/dialog.js';
import '@polymer/paper-button/paper-button.js';
import '@polymer/paper-spinner/paper-spinner-lite.js';
import '@polymer/iron-form/iron-form.js';
import '@cbar/cbar-entity-autocomplete/cbar-entity-autocomplete.js';
import '@cbar/cbar-entity-relationship-selector/cbar-entity-relationship-selector.js';
import '@cbar/cbar-shared-styles/cbar-shared-styles.js';
import '@polymer/paper-checkbox/paper-checkbox.js';
import '@cbar/cbar-textfield/cbar-textfield.js';

/**
 * `cbar-add-person`
 *
 * @customElement
 * @polymer
 * @demo demo/index.html
 */
class CbarAddPerson extends PolymerElement {
  static get template() {
    return html`
      <style include="cbar-shared-styles">
        /*
        Styling for the subcomponents of this element, primarily designed to be
        able to fit small screens or overwriting default styling in order to match
        the site's current styling
        */

      :host {
        display: block;

        --cbar-date-time-picker-label-buttons: {
            justify-content: flex-end;
        };
      }

      [hidden] {
        display: none;
      }

      #dialog {
        min-width: 300px;
      }

      .flex-container {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
      }

      md-dialog {
        overflow: visible;
      }

      #contextRelationshipSelector {
        --cbar-entity-relationship-selector-entity-dropdown: {
          /* Hide the entity dropdown when we're in a context */
          display: none;
        }
      }
      
      paper-spinner{
        padding-right: 16px;
      }

      cbar-entity-relationship-selector {
        --cbar-entity-relationship-selector-entity-dropdown: {
          display: block;
        }

        --cbar-entity-relationship-selector-relationship-dropdown: {
          display: block;
        }

        width: 100%;
      }
      
      paper-checkbox{
      margin-top: 10px;
      color: grey;
      --paper-checkbox-unchecked-color: grey;
      --paper-checkbox-label-color: grey;
      }
      
      cbar-textfield, cbar-entity-autocomplete, cbar-entity-relationship-selector{
        --mdc-theme-primary: var(--cbar-sys-color-primary, #004CA2);
      }
      
      cbar-textfield, cbar-entity-autocomplete{
          margin: 8px 0;
      }

      @media screen and (max-width: 990px) {
        /*TODO: paper-dialog removed by md-dialog so should these be removed or retargeted?*/
        /* Full-width dialogs on mobile */
        paper-dialog {
          left: 0;
          right: 0;

          max-width: 100%;
          margin-left: 5px;
          margin-right: 5px;
        }
      }
    </style>

    <md-dialog id="dialog" open="{{opened}}" on-iron-overlay-opened="_dialogOpened" on-opened-changed="_openedChanged" on-close="__closeHandler">
    <div slot="content">
      <iron-form id="form">
        <form class="flex-container">
          <cbar-entity-autocomplete
            label="First Name"
            source="[[autocompleteItems]]"
            text-property="firstName"
            value-property="id"
            id="firstName"
            dense
            on-item-selected="onAutocompleteChanged"
            fixed-menu-position 
            required
            validation-message="You Must Enter A Valid First Name!"
            hide-search$="[[hideSearch]]"
          ></cbar-entity-autocomplete>

          <cbar-textfield dense label="Last Name" required name="last_name" validationMessage="You Must Enter A Valid Last Name!" id="lastName" pattern="^[a-zA-Z0-9-' ]*$"></cbar-textfield>
          <cbar-textfield dense label="Email Address" data-label="Email Address" name="email" id="email" error-message="You Must Enter A Valid Email!" type="email"></cbar-textfield>
          <cbar-textfield dense label="Mobile Phone"  data-label="Mobile Phone" name="mobile" id="mobilePhone" error-message="You Must Enter A Valid Phone Number!" maxlength="25"></cbar-textfield>

          <template is="dom-if" if="[[!context]]" restamp>
            <!--If we don't have a context, show all the available entity dropdowns.-->
            <template is="dom-repeat" items="[[_groupedEntities]]">
                <cbar-entity-relationship-selector
                  class="no-context"
                  relationship-required
                  disabled$="[[loading]]"  
                  entity-type="[[item.entityType]]"
                  on-selected-entity-changed="_selectedEntityChanged"
                  on-selected-relationship-changed="_selectedEntityChanged"
                  entities="[[item.entities]]"
                  relationships-label="Positions"
                  fixed-menu-position$="[[fixedMenuPosition]]"
                >
                </cbar-entity-relationship-selector>
            </template>
          </template>

          <template is="dom-if" if="[[context]]" restamp>
            <!--If we do have a context, show only the available roles for the given context.-->
            <cbar-entity-relationship-selector
              id="contextRelationshipSelector"
              relationship-required
              disabled$="[[loading]]"  
              entities="[[__getContextEntities(context)]]"
              selected-entity="[[context]]"
              on-selected-entity-changed="_selectedEntityChanged"
              on-selected-relationship-changed="_selectedEntityChanged"
              relationships-label="Positions"
              fixed-menu-position$="[[fixedMenuPosition]]"
            >
            </cbar-entity-relationship-selector>
          </template>

          <paper-checkbox hidden$="[[!showInvite]]" checked="{{sendInvite}}" disabled="[[forceInvite]]">Invite</paper-checkbox>
        </form>
      </iron-form>
      </div>
      <div slot="actions">
        <div hidden\$="[[loading]]">
          <paper-button on-tap="close">Cancel</paper-button>
          <paper-button on-tap="submit">Submit</paper-button>
        </div>

        <div id="submit_inprogress" hidden\$="[[!loading]]" class="hide">
          <paper-spinner-lite active="" class="green"></paper-spinner-lite>
        </div>
      </div>
    </md-dialog>
`;
  }

  static get is() {
    return 'cbar-add-person';
  }

  static get properties() {
    return {
      /**
       * Whether the add person dialog is opened or not
       *
       * @type {Boolean}
       */
      opened: {
        type: Boolean,
        notify: true,
        value: false,
      },

      /**
       * A map of entityType => entities array, where each entity is a CBAR entity
       * object
       *
       * @type {Object}
       */
      entities: Object,

      /**
       * A CBAR entity that will be the context of this add person.
       *
       * Setting this will hide the entities dropdown and only show
       * the relationship dropdown
       *
       * FIXME: what should people be setting this to?
       *
       * @type {Object}
       */
      context: {
        type: Object,
        value: null,
      },

      /**
       * The source array for the autocompletion on the first name field
       *
       * @type {Array}
       */
      autocompleteSource: {
        type: Array,
        value: [],
        observer: '_autocompleteSourceChanged',
      },

      /**
       * The query function that gets called when a user starts
       * typing in the first name field.
       *
       * Receives two parameters: `source` and `query`
       *
       * @type {Function}
       */
      autocompleteQueryFn: {
        type: Function,
      },

      /**
       * Whether the form is currently loading.
       *
       * Adds a spinner and hides cancel & submit buttons if true.
       *
       * @type {Boolean}
       */
      loading: {
        type: Boolean,
        value: false
      },
      showInvite: {
        type: Boolean,
        value: false
      },
      sendInvite: {
        type: Boolean,
        value: false
      },
      forceInvite: {
        type: Boolean,
        value: false
      },
      emailRequired: {
        type: Boolean,
        value: false
      },
      phoneRequired: {
        type: Boolean,
        value: false
      },
      autocompleteItems: {
        type: Array,
        value: []
      },
      fixedMenuPosition: {
        type: Boolean,
        value: false
      },
      selectedId: {
        type: String,
        value: ''
      },
      hideSearch: {
        type: Boolean,
        value: false
      }
    };
  }

  static get observers() {
    return [
      '_contextChanged(entities, context)',
      '_checkOptional(emailRequired, phoneRequired, opened)',
    ];
  }

  /**
   * Returns the `md-dialog` instance in the component's shadow DOM.
   *
   * @returns {HTMLElement} The dialog element.
   */
  get dialog() {
    if (!this._dialog) {
      this._dialog = this.root.querySelector('md-dialog');
    }
    return this._dialog;
  }

  ready() {
    super.ready();
    requestAnimationFrame(() => this.handleOverflowIssue());

    this.$.form.addEventListener('iron-overlay-opened', (e) => {
      // We use e.composedPath due to event re-targeting
      // (see https://www.polymer-project.org/2.0/docs/devguide/events#retargeting)
      let targetDialog = e.composedPath()[0];

      // TODO: paper-dialog replaced by md-dialog so remove or update this
      if (targetDialog.is === 'paper-dialog') {
        // Whenever another dialog opens, close this one and append the opened dialog's shadow-root's host
        // as a child of the document body.
        // We use the root node (shadow-root) to ensure that any shadow-dom styles are applied to the dialog.
        let host = this.constructor._getShadowHost(targetDialog);

        // We need a position at which we will re-append this dialog when it is closed.
        // This obviously won't work if the selected sibling/parent node is removed/moved while
        // the dialog is open.
        let {nextSibling, parentNode} = host;

        // Append the dialog's host to the document body.
        document.body.appendChild(host);

        // dialog.close() is slow, so we'll use opacity instead.
        this.$.dialog.style.opacity = '0';

        if (targetDialog.domHost && targetDialog.domHost.showLabelButtons) {
          // Remove date picker label buttons when the dialog is appended to
          // the body
          targetDialog.domHost.showLabelButtons = false;
        }

        let closedEventHandler;
        targetDialog.addEventListener('iron-overlay-closed', closedEventHandler = () => {
          targetDialog.removeEventListener('iron-overlay-closed', closedEventHandler);

          if (targetDialog.domHost && targetDialog.domHost.showLabelButtons === false) {
            // Show date picker label buttons when the dialog is back in our dialog
            targetDialog.domHost.showLabelButtons = true;
          }

          this.$.dialog.style.opacity = '1';

          if (nextSibling) {
            // If we had a sibling, insert it back before the sibling.
            parentNode.insertBefore(host, nextSibling);
          } else {
            // Just append to the end of the childNodes list.
            parentNode.appendChild(host);
          }
        });
      }
    });
  }

  _autocompleteSourceChanged(autocompleteSource) {
    if (!autocompleteSource) {
      return;
    }

    this.autocompleteItems = autocompleteSource.map(item => ({
      id: item.id,
      firstName: item.firstName,
      title: `${item.firstName} ${item.lastName}`,
      imageUri: item.imageUri,
      description: item.email,
      displayText: item.displayName,
    }));
  }

  _checkOptional(emailRequired, phoneRequired, opened) {
    if (opened === false) {
      return;
    }

    let propertiesToCheck = [emailRequired, phoneRequired];
    let elementsToCheck = this.root.querySelectorAll('#email, #mobilePhone');

    elementsToCheck.forEach((element, i) => {
      if (propertiesToCheck[i] === false) {
        element.label = `${element.dataset.label} (Optional)`;
      } else {
        element.required = true;
      }
    });
  }

  /**
   * Clones and bubbles the given event up one level (unless 'bubbles'
   * is true, in which case it will bubble indefinitely)
   *
   * @param e
   * @private
   */
  _selectedEntityChanged(e) {
    let cloned = new e.constructor(e.type, e);
    this.dispatchEvent(cloned);
  }

  /**
   * Traverses the ancestors of the passed element until it finds a shadow-root
   * and returns the shadow-root's 'host' element, or returns the ele itself if
   * it reaches the document root node.
   *
   * @param {HTMLElement} ele
   *
   * @return HTMLElement
   */
  static _getShadowHost(ele) {
    let retEle = ele;

    while (retEle.nodeType !== 11) {
      retEle = retEle.parentNode;
    }

    return retEle.nodeType === 11 ? retEle.host : ele;
  }

  static get _shouldLockScroll() {
    // If we don't have a scrollTop on the body, but we have one on
    // the HTML element, ignore. This can happen on the desktop site of
    // Campaign Central.
    let sameBodyAndHtmlScroll = document.body.scrollTop === document.documentElement.scrollTop;

    return document.body.scrollTop > 0 || sameBodyAndHtmlScroll;
  }

  /**
   * Sets a scroll lock on the body when the dialog is opened, preventing
   * issues in iOS when attempting to focus input fields in the dialog.
   */
  _dialogOpened() {
    // Unfortunately, we must listen for the iron-overlay-opened event
    // separately from the closed (i.e. using on-opened-changed) because
    // the iron-overlay-opened event fires after the dialog is actually
    // opened, whereas opened-changed fires before, causing intermittent issues
    // with positioning of the dialog.
    if (!this.constructor._shouldLockScroll) {
      return;
    }

    this._scrollOffset = document.body.scrollTop;

    // Essentially lock the scrolling of the body, but set the top offset
    // to keep the illusion of it having not moved since the dialog was opened.
    document.body.style.position = 'fixed';
    document.body.style.top = (this._scrollOffset * -1) + 'px';
    document.body.style.left = 0;
    document.body.style.right = 0;
  }

  _openedChanged(e) {
    if (!this.constructor._shouldLockScroll) {
      return;
    }

    let opened = e.detail.value;

    if (!opened) {
      // Check if the dialog was closed and reset the body to static
      // position and re-adjust the scroll position to what it was before
      // the dialog was opened.
      document.body.style.position = 'static';
      document.body.scrollTop = this._scrollOffset;
    }
  }

  _contextChanged(entities, context) {
    if (typeof entities === 'object' && !context) {
      let entityTypes = Object.keys(entities);

      // Group the entities into a format suitable for a dom-repeat
      this._groupedEntities = entityTypes.map(entityType => {
        let groupedEntities = entities[entityType];

        return {
          entityType,
          entities: groupedEntities,
        };
      });
    }

    requestAnimationFrame(() => {
      // Prepare our cbar-entity-relationship-selector elements for form submission
      let selector = context ? '#contextRelationshipSelector' : 'cbar-entity-relationship-selector.no-context';
      this._entityRelationshipElements = this.root.querySelectorAll(selector);
    });
  }

  __getContextEntities(context) {
    return [context];
  }

  getPerson(id) {
    return this.autocompleteSource.find(item => item.id === id);
  }

  onAutocompleteChanged(e) {
    const {value} = e.detail;

    // Find the matching person
    const person = this.getPerson(value);
    if (!person) {
      return;
    }

    this.selectedId = value;
    this.$.lastName.value = person.lastName;
    this.$.email.value = person.email ? person.email : '';
  }

  open() {
    this.opened = true;
    requestAnimationFrame(() => this.handleOverflowIssue());
  }

  /**
   * Closes the dialog and resets the form.
   */

  //TODO: Consider using __closeHandler but that breaks Organizer now
  close() {
    this.$.firstName.value = this.$.firstName.value = '';
    this.$.firstName.invalid = false;
    this.$.lastName.value = '';
    this.$.lastName.invalid = false;
    this.$.email.value = '';
    this.$.email.invalid = false;
    this.$.mobilePhone.value = '';
    this.$.mobilePhone.invalid = false;
    this.opened = false;
  }

  getFormDetails() {
    if (!this._entityRelationshipElements) {
      this._entityRelationshipElements = [];
    }

    let entityRelationships = [];
    this._entityRelationshipElements.forEach(element => {
      if (!element.selectedEntity) {
        return;
      }

      entityRelationships.push({
        entityType: element.entityType,
        selectedEntity: element.selectedEntity,
        selectedRelationship: element.selectedRelationship,
        selectedStartDate: element.selectedStartDate,
        selectedEndDate: element.selectedEndDate,
      });
    });

    return {
      firstName: this.$.firstName.value,
      lastName: this.$.lastName.value,
      email: this.$.email.value,
      mobile: this.$.mobilePhone.value,
      entityRelationships,
    };
  }

  validate() {
    let elementsToValidate = this.$.form._getValidatableElements();
    let isValid = true;

    elementsToValidate.forEach(element => {
      let elementIsValid = true;
      if(typeof element.validate  === 'function'){
        elementIsValid = element.validate();

        if(isValid && !elementIsValid){
          isValid = false;
        }
      } else {
        elementIsValid = element.reportValidity();

        if(isValid && !elementIsValid){
          isValid = false;
        }
      }
    });

    return isValid;
  }

  submit() {
    if (!this.validate()) {
      return;
    }

    const detail = this.getFormDetails();

    // Dispatch the submit event
    this.dispatchEvent(new CustomEvent('submit', {
      detail,
      bubbles: true,
      composed: true,
    }));
  }

  //TODO: Determine when to call this and test against Organizer
  resetForm() {
    this.$.firstName.value = '';
    this.$.firstName.invalid = false;
    this.$.lastName.value = '';
    this.$.lastName.invalid = false;
    this.$.email.value = '';
    this.$.email.invalid = false;
    this.$.mobilePhone.value = '';
    this.$.mobilePhone.invalid = false;

    // Reset any additional fields or components
    if (this._entityRelationshipElements) {
      this._entityRelationshipElements.forEach(element => {
        element.selectedEntity = null;
        element.selectedRelationship = null;
      });
    }
  }

  __closeHandler(e){
    if(this.loading){
      e.preventDefault();
    } else {
      this.opened = false;
    }
  }

  /**
   * Fixes overflow issues for the dialog container and scroller.
   */
  handleOverflowIssue() {
    this.dialog.container.style.overflow = 'visible';
    this.dialog.scroller.style.overflow = 'visible';
  }

}

window.customElements.define(CbarAddPerson.is, CbarAddPerson);
